import React, {
  ComponentProps,
  memo,
  useCallback,
  useEffect,
} from 'react';
import forIn from 'lodash/forIn';
import keys from 'lodash/keys';
import times from 'lodash/times';
import constant from 'lodash/constant';
import zipObject from 'lodash/zipObject';
import pick from 'lodash/pick';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  BillingState,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  billFields,
  visitNoteFields,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  usePrompt,
} from 'core/hooks';
import VisitNoteFormActions from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteFormActions';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import {
  VisitNoteDTO,
} from 'dtos';

type BillingButtonProps = ComponentProps<typeof VisitNoteFormActions>['billingButtonProps'];

type Props = {
  isFinalized: boolean;
  sendDocumentsActions: ComponentProps<typeof VisitNoteFormActions>['sendDocumentsActions'];
  savingVisitNote: boolean;
  unfinalizingVisitNote: boolean;
  isPrinting: boolean;
  isFinalizedPdfPending: boolean;
  onSave: (data: VisitNoteData) => Promise<void>;
  onFinalize: (data: VisitNoteData) => Promise<VisitNoteDTO | null>;
  onUnfinalize: (data: VisitNoteData) => Promise<VisitNoteDTO | null>;
  onUpdateBillingState: (data: VisitNoteData) => Promise<VisitNoteDTO | null>;
  onPrint?: (data: VisitNoteData) => Promise<void>;
};

const billingButtonsProps: Record<BillingState, BillingButtonProps> = {
  [BillingState.NotReady]: {
    variant: 'contained',
    color: 'primary',
    children: 'Mark for Billing Review',
    nextState: BillingState.Review,
  },
  [BillingState.Review]: {
    variant: 'outlined',
    color: 'primary',
    className: '!border-secondary disabled:!border-inherit',
    children: 'Complete Billing Review',
    nextState: BillingState.ReviewCompleted,
  },
  [BillingState.ReviewCompleted]: {
    variant: 'outlined',
    color: 'primary',
    children: 'Billing Review Completed',
  },
};

function VisitNoteFooter({
  isFinalized,
  sendDocumentsActions,
  savingVisitNote,
  unfinalizingVisitNote,
  isPrinting,
  isFinalizedPdfPending,
  onSave,
  onFinalize,
  onUnfinalize,
  onUpdateBillingState,
  onPrint,
}: Props) {
  const {
    handleSubmit,
    reset,
    formState: {
      isDirty,
      isSubmitting,
      isValid,
      dirtyFields,
    },
    control,
  } = useFormContext<VisitNoteData>();

  const [
    billingState,
    updatedOn,
    serviceCodes = [],
    customServiceCodes = [],
  ] = useWatch({
    control,
    name: [
      'bill.state',
      'updatedOn',
      'bill.serviceCodes',
      'bill.customServiceCodes',
    ],
  }) as [
    BillingState | undefined,
    string | undefined,
    VisitNoteData['bill']['serviceCodes'],
    VisitNoteData['bill']['customServiceCodes'],
  ];

  const billingStateValue = billingState ?? BillingState.NotReady;
  const billingButtonProps = billingButtonsProps[billingStateValue];
  const isBillingReviewCompleted = billingStateValue === BillingState.ReviewCompleted;
  const isBillingReview = billingStateValue === BillingState.Review;
  const hasBillingCodes = serviceCodes.length > 0 || (customServiceCodes ?? []).length > 0;
  const fields = isFinalized ? billFields : visitNoteFields;

  const dirty: string[] = [];
  forIn(dirtyFields, (value, key) => {
    if (typeof value === 'boolean') {
      dirty.push(key);
    } else {
      dirty.push(...keys(value).map((childKey) => `${key}.${childKey}`));
    }
  });
  const dirtyFieldsCount = size(pick(
    zipObject(dirty, times(dirty.length, constant(true))),
    fields,
  ));

  usePrompt(dirtyFieldsCount > 0);

  const saveData = useCallback(async (data: VisitNoteData) => {
    try {
      await onSave(data);
      reset(data, {
        keepDirty: false,
        keepTouched: false,
        keepIsSubmitted: false,
      });
    } catch {
      // Parent already logs/notifies; preserve dirty form state
    }
  }, [onSave, reset]);

  const finalizeData = useCallback(async (data: VisitNoteData) => {
    try {
      const updatedData = await onFinalize(data);
      if (!isNil(updatedData)) {
        reset({
          ...data,
          isFinalized: true,
          version: updatedData.version ?? data.version,
        }, {
          keepDirty: false,
          keepTouched: false,
          keepIsSubmitted: false,
        });
      }
    } catch {
      // Parent already logs/notifies; preserve dirty form state
    }
  }, [onFinalize, reset]);

  const unfinalizeData = useCallback(async (data: VisitNoteData) => {
    try {
      const updatedData = await onUnfinalize(data);
      reset({
        ...data,
        isFinalized: false,
        version: updatedData?.version ?? data?.version,
      }, {
        keepDirty: false,
        keepTouched: false,
        keepIsSubmitted: false,
      });
    } catch {
      // Parent already logs/notifies; preserve dirty form state
    }
  }, [onUnfinalize, reset]);

  const updateBillingStateData = useCallback(async (data: VisitNoteData) => {
    try {
      const updatedData = await onUpdateBillingState(data);
      reset({
        ...data,
        version: updatedData?.version ?? data?.version,
        bill: {
          ...(data.bill ?? {}),
          state: billingButtonProps.nextState ?? BillingState.Review,
        },
      }, {
        keepDirty: false,
        keepTouched: false,
        keepIsSubmitted: false,
      });
    } catch {
      // Parent already logs/notifies; preserve dirty form state
    }
  }, [billingButtonProps.nextState, onUpdateBillingState, reset]);

  const handleSaveClick = useCallback(() => {
    handleSubmit(saveData)();
  }, [handleSubmit, saveData]);

  const handleFinalizeClick = useCallback(() => {
    handleSubmit(finalizeData)();
  }, [finalizeData, handleSubmit]);

  const handleUnfinalizeClick = useCallback(() => {
    handleSubmit(unfinalizeData)();
  }, [handleSubmit, unfinalizeData]);

  const handleUpdateBillingStateClick = useCallback(() => {
    handleSubmit(updateBillingStateData)();
  }, [handleSubmit, updateBillingStateData]);

  const handlePrintClick = useCallback(() => {
    if (!isNil(onPrint)) {
      handleSubmit(onPrint)();
    }
  }, [handleSubmit, onPrint]);

  const handleSaveAsTemplate = useCallback(() => {
    visitNoteEventChannel.emit('onSaveAsMasterTemplate');
  }, []);

  useEffect(() => visitNoteEventChannel.on('onRequestSave', handleSaveClick), [handleSaveClick]);

  return (
    <VisitNoteFormActions
      isDirty={isDirty}
      dirtyFieldsCount={dirtyFieldsCount}
      isSubmitting={isSubmitting}
      isValid={isValid}
      savingVisitNote={savingVisitNote}
      unfinalizingVisitNote={unfinalizingVisitNote}
      isFinalized={isFinalized}
      sendDocumentsActions={sendDocumentsActions}
      isBillingReview={isBillingReview}
      isBillingReviewCompleted={isBillingReviewCompleted}
      hasBillingCodes={hasBillingCodes}
      updatedOn={updatedOn}
      billingButtonProps={billingButtonProps}
      onSave={handleSaveClick}
      onFinalize={handleFinalizeClick}
      onUnfinalize={handleUnfinalizeClick}
      onUpdateBillingState={handleUpdateBillingStateClick}
      onSaveAsTemplate={handleSaveAsTemplate}
      onPrint={handlePrintClick}
      isPrinting={isPrinting}
      isFinalizedPdfPending={isFinalizedPdfPending}
    />
  );
}

export default memo(VisitNoteFooter);
